/**
 * Layman-friendly subject-area names keyed by cluster_id (from DataLab step-23).
 * @type {Record<number, string>}
 */
export const CLUSTER_FRIENDLY_NAMES = {
  16: 'Law & Business',
  20: 'Accounting',
  24: 'Finance & FinTech',
  9: 'Economics & Maths',
  11: 'Business & Marketing',
  17: 'Global Business',
  13: 'Management & Operations',
  12: 'Hospitality & Tourism',
  2: 'Civil Engineering',
  14: 'Architecture & Urban Design',
  27: 'Environmental Science',
  32: 'Systems Engineering',
  15: 'Computer Engineering',
  31: 'Biomedical Engineering',
  8: 'Science & Research',
  22: 'Data & Computing',
  33: 'Artificial Intelligence',
  7: 'Technology & Design',
  29: 'Media & Communication',
  5: 'Music & Digital Arts',
  30: 'Fine Arts & Film',
  3: 'History & Philosophy',
  25: 'Chinese & Humanities',
  34: 'English & Languages',
  19: 'Language Education',
  6: 'Early Childhood Education',
  1: 'Psychology & Social Science',
  0: 'Social Work & Community',
  28: 'Sociology & Public Policy',
  21: 'Sports & Coaching',
  18: 'Medical Laboratory Science',
  26: 'Biomedical & Health',
  4: 'Physiotherapy & Therapy',
  10: 'Nursing & Health Care',
  23: 'Medicine & Pharmacy'
};

/** Broad subject families for tile colour theming. */
export const CLUSTER_SUBJECT_FAMILY = {
  16: 'business',
  20: 'business',
  24: 'business',
  9: 'business',
  11: 'business',
  17: 'business',
  13: 'business',
  12: 'business',
  2: 'engineering',
  14: 'engineering',
  27: 'engineering',
  32: 'engineering',
  15: 'engineering',
  31: 'engineering',
  8: 'engineering',
  22: 'technology',
  33: 'technology',
  7: 'technology',
  29: 'creative',
  5: 'creative',
  30: 'creative',
  3: 'humanities',
  25: 'humanities',
  34: 'humanities',
  19: 'humanities',
  6: 'humanities',
  1: 'social',
  0: 'social',
  28: 'social',
  21: 'health',
  18: 'health',
  26: 'health',
  4: 'health',
  10: 'health',
  23: 'health'
};

/**
 * Subject family key for tile styling.
 * @param {{ cluster_id?: number }} cluster
 * @returns {'business'|'engineering'|'technology'|'creative'|'humanities'|'social'|'health'|'general'}
 */
export function interestClusterSubjectFamily(cluster) {
  const id = Number(cluster?.cluster_id);
  if (Number.isFinite(id) && CLUSTER_SUBJECT_FAMILY[id]) {
    return CLUSTER_SUBJECT_FAMILY[id];
  }
  return 'general';
}

const AREA_WORD_OVERRIDES = {
  ai: 'AI',
  fintech: 'FinTech',
  it: 'IT',
  hr: 'HR',
  uk: 'UK',
  usa: 'USA'
};

function titleCaseWord(word) {
  const lower = String(word || '').trim().toLowerCase();
  if (!lower) return '';
  if (AREA_WORD_OVERRIDES[lower]) return AREA_WORD_OVERRIDES[lower];
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function titleCasePhrase(phrase) {
  return String(phrase || '')
    .trim()
    .split(/\s+/)
    .map(titleCaseWord)
    .join(' ');
}

function keywordParts(cluster) {
  const raw = String(cluster?.label || cluster?.theme || '').trim();
  if (!raw) return [];
  const parts = raw.split(',').map((s) => s.trim()).filter((p) => p.length > 1);
  const deduped = [];
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (deduped.some((d) => lower === d.toLowerCase() || lower.includes(d.toLowerCase()) || d.toLowerCase().includes(lower))) {
      continue;
    }
    deduped.push(part);
  }
  return deduped;
}

/**
 * Pick how many top clusters to show as tiles (3–5) based on score gaps after #3.
 * @param {Array<{ score: number }>} clusters sorted descending
 * @returns {number} 1–5
 */
export function pickInterestTileCount(clusters) {
  if (!Array.isArray(clusters) || clusters.length === 0) return 0;
  if (clusters.length <= 3) return clusters.length;

  let count = 3;
  const gap34 = (clusters[2]?.score || 0) - (clusters[3]?.score || 0);
  if (clusters.length >= 4 && isCloseGap(gap34, clusters[2]?.score)) {
    count = 4;
    const gap45 = (clusters[3]?.score || 0) - (clusters[4]?.score || 0);
    if (clusters.length >= 5 && isCloseGap(gap45, clusters[3]?.score)) {
      count = 5;
    }
  }
  return count;
}

/** @param {number} gap absolute score gap (0–1) */
function isCloseGap(gap, higherScore) {
  if (gap <= 0) return true;
  const absGapPct = gap * 100;
  const relGap = higherScore > 0 ? gap / higherScore : 1;
  return absGapPct <= 1.5 || relGap <= 0.22;
}

/**
 * Short layman-facing title for a cluster tile.
 * @param {{ cluster_id?: number, label?: string, theme?: string }} cluster
 */
export function interestClusterTitle(cluster, t) {
  const id = Number(cluster?.cluster_id);
  if (Number.isFinite(id) && typeof t === 'function') {
    const translated = t(`interestCluster.categories.${id}`, {
      defaultValue: CLUSTER_FRIENDLY_NAMES[id] || ''
    });
    if (translated) return translated;
  }
  if (Number.isFinite(id) && CLUSTER_FRIENDLY_NAMES[id]) {
    return CLUSTER_FRIENDLY_NAMES[id];
  }
  const parts = keywordParts(cluster);
  if (!parts.length) return '—';
  const first = titleCasePhrase(parts[0]);
  const second = parts[1] ? titleCasePhrase(parts[1]) : '';
  if (second && second.toLowerCase() !== first.toLowerCase()) {
    return `${first} & ${second}`;
  }
  return first;
}

/**
 * Optional subtitle with example topics (plain language).
 * @param {{ cluster_id?: number, label?: string, theme?: string }} cluster
 * @param {(key: string, opts?: { defaultValue?: string }) => string} [t]
 */
export function interestClusterHint(cluster, t) {
  const id = Number(cluster?.cluster_id);
  if (Number.isFinite(id) && typeof t === 'function') {
    const translated = t(`interestCluster.hints.${id}`, { defaultValue: '' });
    if (translated) return translated;
  }
  const parts = keywordParts(cluster);
  if (parts.length < 3) return '';
  return parts
    .slice(2, 5)
    .map(titleCasePhrase)
    .join(' · ');
}

export function formatInterestPct(score) {
  return `${((score || 0) * 100).toFixed(1)}%`;
}

/** @param {Array<{ score?: number }>} clusters */
export function topClusterScore(clusters) {
  if (!Array.isArray(clusters) || clusters.length === 0) return 0;
  return Math.max(...clusters.map((c) => c.score || 0), 0);
}

/**
 * Relative fit vs the user's best-matching cluster (0–100).
 * @param {number} score
 * @param {number} topScore
 */
export function relativeMatchPct(score, topScore) {
  if (!topScore) return 0;
  return Math.round(((score || 0) / topScore) * 100);
}

/**
 * Layman tier for student-facing tiles (rank 1 is always best).
 * @param {number} relativePct 0–100 vs top cluster
 * @param {number} rank 1-based
 * @returns {'best'|'strong'|'good'|'explore'}
 */
export function interestMatchTier(relativePct, rank) {
  if (rank === 1) return 'best';
  if (relativePct >= 85) return 'strong';
  if (relativePct >= 60) return 'good';
  return 'explore';
}
